import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDocument } from "../hooks/useDocument";
import { useQueryClient } from "react-query";

import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusIcon,
  DocumentIcon,
  TrashIcon,
  LinkIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import ProgressTracker from "./DocProgressTracker";
import PublishedLinkSection from "./PublishedLinkSection";
import SignedDocumentPreview from "./SignedDocumentPreview";
import { Download, Clock, Eye, History } from "lucide-react";
import ErrorPage from "../pages/ErrorPage";
import CapabilitiesStyles from "./CapabilitiesStyles";
import TemplateSelector from "./TemplateSelector";
import Modal from "./Modal";
import AddClientForm from "./AddClientForm";
import ClientSearchSelect from "./ClientSearchSelect";
import { formatHtml, isHtmlUnformatted } from "../functions/formatHtml";

const DocumentAnalytics = ({ document }) => {
  // Only show analytics if the document has been shared
  if (!document?.shareable_link) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-darkbg-800 rounded-lg shadow-md p-4 sm:p-6 mb-6">
      <h3 className="text-lg font-semibold text-grayteck-900 dark:text-grayteck-100 mb-4 pb-2 border-b border-grayteck-200 dark:border-grayteck-700">
        Document Analytics
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {/* View Count */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="ml-3 text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
                Total Views
              </h4>
            </div>
            <span className="text-2xl font-bold text-grayteck-900 dark:text-grayteck-100">
              {document.open_count || 0}
            </span>
          </div>
        </div>

        {/* First View */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
              <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <h4 className="ml-3 text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
              First Viewed
            </h4>
          </div>
          <p className="text-sm text-grayteck-600 dark:text-grayteck-300">
            {document.first_opened_at
              ? new Date(document.first_opened_at).toLocaleString()
              : "Not viewed yet"}
          </p>
        </div>

        {/* Last View */}
        <div className="bg-grayteck-50 dark:bg-darkbg-700 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full">
              <History className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h4 className="ml-3 text-sm font-medium text-grayteck-900 dark:text-grayteck-100">
              Last Viewed
            </h4>
          </div>
          <p className="text-sm text-grayteck-600 dark:text-grayteck-300">
            {document.last_opened_at
              ? new Date(document.last_opened_at).toLocaleString()
              : "Not viewed yet"}
          </p>
        </div>
      </div>
    </div>
  );
};

const DocumentEditor = () => {
  const { id } = useParams();
  const location = useLocation();

  // ... (other state variables)
  const [selectedClient, setSelectedClient] = useState(""); // New state for selected client

  const navigate = useNavigate();

  // Initialize useDocument hook
  const {
    document,
    isLoading,
    isError,
    createDocument,
    updateDocument,
    publishDocument,
    unpublishDocument, // Add this
    downloadDocument,
    isDownloading,
    isPublishing, // Add this
    isUnpublishing, // Add this
  } = useDocument(id);

  // Selected client object, populated by ClientSearchSelect (either from a
  // direct user pick or from its by-id lookup on hydration). Used to autofill
  // tagline / company fields below.
  const [selectedClientData, setSelectedClientData] = useState(null);

  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [secondaryTagline, setSecondaryTagline] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [description, setDescription] = useState(""); // New state for description
  const [content, setContent] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasSignatureBox, setHasSignatureBox] = useState(false);
  // Multi-signer mode: mutually exclusive with the single signature box. When on,
  // the document requires every listed signer to sign before it locks.
  const [multiSignerMode, setMultiSignerMode] = useState(false);
  const [signers, setSigners] = useState([{ name: "" }, { name: "" }]);
  const [bannerImage, setBannerImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishActionLoading, setIsPublishActionLoading] = useState(false);
  const [publishedLink, setPublishedLink] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [documentType, setDocumentType] = useState("standard");
  const [activeTemplate, setActiveTemplate] = useState("standard");
  // Settings: whether payment and support selection is required before signing
  const [requirePaymentSelection, setRequirePaymentSelection] = useState(true);
  const [requireSupportSelection, setRequireSupportSelection] = useState(true);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false); // State for modal
  const previewRef = useRef(null);
  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Autofill tagline / company fields when a client is picked or hydrated.
  // selectedClientData is supplied by ClientSearchSelect (either from a user
  // pick or from its by-id lookup on edit-mode hydration).
  useEffect(() => {
    if (selectedClient && selectedClientData) {
      const client = selectedClientData;
      if (client.client_type === "business") {
        setTagline(client.contact_info?.primary_contact || "");
        setSecondaryTagline(client.contact_info?.position || "");
        setClientCompany(client.name);
      } else {
        // Individual client: use their name and leave role empty.
        setTagline(client.name);
        setSecondaryTagline("");
        setClientCompany("");
      }
    } else if (!selectedClient) {
      // Clear all fields when no client is selected.
      setTagline("");
      setSecondaryTagline("");
      setClientCompany("");
    }
  }, [selectedClient, selectedClientData]);

  useEffect(() => {
    if (document) {
      // Hydrate the picker from the loaded document. The picker resolves the
      // client name by id and bubbles the full object back via onClientSelect.
      setSelectedClient(document.client_id || "");
    }
  }, [document]);

  // Main document effect for state management
  useEffect(() => {
    if (document) {
      const prevContent = content;
      const prevHasSignatureBox = hasSignatureBox;

      // Only update states if it's the initial load or if we're not in the middle of saving
      if (isInitialMount.current || !isSaving) {
        setTitle(document.title);
        setDescription(document.description || "");
        setSubtitle(document.subtitle || "");
        setTagline(document.tagline || "");
        setSecondaryTagline(document.secondary_tagline || "");
        setClientCompany(document.receiving_company || "");
        setBannerImage(document.banner_image);
        setDocumentType(document.document_type || "standard");
        setActiveTemplate(document.document_type || "standard");

        // Handle signature box and content
        const hasSignature = document.content.includes("signature-container");
        const contentWithoutSignature = document.content
          .replace(/<div class="signature-container">[\s\S]*$/, "")
          .trim();

        setContent(contentWithoutSignature);

        // Multi-signer docs carry a signers list; they take precedence over the
        // single signature box (the two are mutually exclusive).
        const docSigners = Array.isArray(document.signers) ? document.signers : [];
        if (docSigners.length > 0) {
          setMultiSignerMode(true);
          setSigners(docSigners.map((s) => ({ name: s.name || "" })));
          setHasSignatureBox(false);
        } else {
          setMultiSignerMode(false);
          setHasSignatureBox(hasSignature);
        }

        // Only reset saved states if we're not in the middle of a save operation
        if (!isSaving) {
          setIsSaved(true);
          setHasChanges(false);
        }
      }

      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
    }
  }, [document]);

  // Change detection effect
  useEffect(() => {
    if (!isInitialMount.current && !isSaving) {
      const currentContent = hasSignatureBox
        ? content + signatureBoxHtml
        : multiSignerMode
        ? content + multiSignerMarker
        : content;
      const documentContent = document?.content || "";

      const hasContentChanged = currentContent !== documentContent;
      const hasTitleChanged = title !== document?.title;
      const hasDescriptionChanged =
        description !== (document?.description || "");
      const hasBannerChanged = bannerImage !== document?.banner_image;
      const hasSignatureBoxChanged =
        hasSignatureBox !== document?.content.includes("signature-container");
      // Add checks for capabilities-specific fields
      const hasSubtitleChanged = subtitle !== (document?.subtitle || "");
      const hasTaglineChanged = tagline !== (document?.tagline || "");
      const hasSecondaryTaglineChanged =
        secondaryTagline !== (document?.secondary_tagline || "");
      const hasSelectedClientChanged =
        selectedClient !== (document?.receiving_company || "");

      const hasAnyChange =
        hasContentChanged ||
        hasTitleChanged ||
        hasDescriptionChanged ||
        hasBannerChanged ||
        hasSignatureBoxChanged ||
        hasSubtitleChanged ||
        hasTaglineChanged ||
        hasSecondaryTaglineChanged ||
        hasSelectedClientChanged;

      if (hasAnyChange) {
        setHasChanges(true);
        setIsSaved(false);
      }
    }
  }, [
    title,
    content,
    description,
    bannerImage,
    hasSignatureBox,
    multiSignerMode,
    signers,
    subtitle, // Add capabilities fields to dependencies
    tagline,
    secondaryTagline,
    selectedClient,
    document,
    isSaving,
  ]);

  const loadSignaturePadScript = () => {
    if (typeof window !== "undefined" && window.document) {
      const script = window.document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/signature_pad/1.5.3/signature_pad.min.js";
      script.async = true;
      window.document.body.appendChild(script);

      script.onload = initializeSignaturePad;

      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  };

  // Update handlePublish and add handleUnpublish
  const handlePublishAction = async () => {
    if (!id) {
      toast.error("Please save the document before publishing.");
      return;
    }
    if (!title || !content) {
      toast.error("Please provide a title and content before publishing.");
      return;
    }

    setIsPublishActionLoading(true);
    try {
      if (document?.shareable_link) {
        // Unpublish
        await unpublishDocument();
        // Refresh document data so UI updates automatically
        await queryClient.invalidateQueries(["document", id]);
      } else {
        // Publish
        const publishedDoc = await publishDocument();
        // Refresh document data so UI updates automatically
        await queryClient.invalidateQueries(["document", id]);
        // Extract token and set local published link (optional)
        const token = publishedDoc.shareable_link.split("/").pop();
        const frontendUrl = `${window.location.origin}/share/${token}`;
        setPublishedLink(frontendUrl);
      }
    } catch (error) {
      console.error("Publish/Unpublish error:", error);
      toast.error(
        `Failed to ${
          document?.shareable_link ? "unpublish" : "publish"
        } document. Please try again.`
      );
    } finally {
      setIsPublishActionLoading(false);
    }
  };

  // Update the action buttons section in the render

  useEffect(() => {
    if (document) {
      setTitle(document.title);
      setDescription(document.description || ""); // Set description
      setSubtitle(document.subtitle || "");
      setTagline(document.tagline || "");
      setSecondaryTagline(document.secondary_tagline || "");
      const contentWithoutSignature = document.content
        .replace(/<div class="signature-container">[\s\S]*$/, "")
        .trim();
      setContent(contentWithoutSignature);
      setBannerImage(document.banner_image);
      {
        const docSigners = Array.isArray(document.signers) ? document.signers : [];
        if (docSigners.length > 0) {
          setMultiSignerMode(true);
          setSigners(docSigners.map((s) => ({ name: s.name || "" })));
          setHasSignatureBox(false);
        } else {
          setMultiSignerMode(false);
          setHasSignatureBox(document.content.includes("signature-container"));
        }
      }
      setIsSaved(true);
      setHasChanges(false); // Reset hasChanges after loading document

      setDocumentType(document.document_type || "standard");
      setActiveTemplate(document.document_type || "standard");
      // Load settings for selection requirements
      setRequirePaymentSelection(document.require_payment_selection ?? true);
      setRequireSupportSelection(document.require_support_selection ?? true);
    }
  }, [document]);

  // Reset saved state when content changes
  useEffect(() => {
    if (!isInitialMount.current) {
      setIsSaved(false);
      setHasChanges(true); // Set hasChanges to true when content changes
    } else {
      isInitialMount.current = false;
    }
  }, [title, content, description, bannerImage, hasSignatureBox, requirePaymentSelection, requireSupportSelection]);

  // Also update your handleSave function to reset hasChanges
  const handleSave = async () => {
    if (isSaving || (isSaved && !hasChanges)) return;

    let fullContent = content;

    // Build the ordered signers list from non-empty names (multi-signer mode).
    const cleanedSigners = signers
      .map((s) => (s.name || "").trim())
      .filter((name) => name.length > 0)
      .map((name, index) => ({ name, order: index }));

    if (hasSignatureBox) {
      fullContent += signatureBoxHtml;
    } else if (multiSignerMode && cleanedSigners.length > 0) {
      // Append the marker; the backend renders one signature line per signer.
      fullContent += multiSignerMarker;
    }

    // Base document data
    const documentData = {
      title,
      description,
      content: fullContent,
      banner_image: bannerImage,
      document_type: documentType,
      // Persist signer configuration. Always send both so switching modes (or
      // turning signing off) clears the previous setup on the saved document.
      signers: multiSignerMode && cleanedSigners.length > 0 ? cleanedSigners : null,
      signature_doc: hasSignatureBox || (multiSignerMode && cleanedSigners.length > 0),
    };

    // Add client reference when a client is selected
    // if (selectedClient) {
    //   documentData.client_id = selectedClient;
    // }
    documentData.client_id = selectedClient || null;

    // Add template-specific fields
    if (activeTemplate === "capabilities") {
      documentData.subtitle = subtitle;
      documentData.tagline = tagline;
      documentData.secondary_tagline = secondaryTagline;
    } else if (activeTemplate === "proposal") {
      documentData.subtitle = subtitle;
      documentData.poc_name = tagline;
      documentData.poc_role = secondaryTagline;
      // Always use clientCompany name for receiving_company in proposals
      documentData.receiving_company = clientCompany;
    }
    // Save settings for selection requirements. These only apply to proposals
    // (which render payment/support cards). Forcing them off for other document
    // types prevents a standard signature doc from being unsignable because it
    // demands a payment option that does not exist on the page.
    const isProposal = activeTemplate === "proposal";
    documentData.require_payment_selection = isProposal ? requirePaymentSelection : false;
    documentData.require_support_selection = isProposal ? requireSupportSelection : false;

    setIsSaving(true);
    try {
      if (!id) {
        // Creating new document
        const newDoc = await createDocument(documentData);

        if (newDoc?._id) {
          // Navigate first
          navigate(`/edit/${newDoc._id}`, { replace: true });
          // Let the document effect handle state updates
          await queryClient.invalidateQueries(["document", newDoc._id]);
          toast.success("Document created successfully!");
        }
      } else {
        // Updating existing document
        await updateDocument({ id, ...documentData });

        // Don't immediately reset states - wait for the effect to complete
        queryClient.setQueryData(["document", id], (oldData) => ({
          ...oldData,
          ...documentData,
        }));
        setIsSaved(true);
        setHasChanges(false); // Reset hasChanges after successful save
        toast.success("Document updated successfully!");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save document. Please try again.");
      setIsSaved(false);
      setHasChanges(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Add a new effect to handle navigation success
  useEffect(() => {
    if (document) {
      const hadChanges = hasChanges; // Store current change state

      setTitle(document.title);
      setDescription(document.description || "");
      setSubtitle(document.subtitle || "");

      // Set specific values for the "proposal" document type
      if (document.document_type === "proposal") {
        setTagline(document.poc_name || ""); // Set value for "Point of Contact Name"
        setSecondaryTagline(document.poc_role || ""); // Set value for "Point of Contact Role"
      } else {
        setTagline(document.tagline || ""); // Standard tagline
        setSecondaryTagline(document.secondary_tagline || ""); // Standard secondary tagline
      }

      const contentWithoutSignature = document.content
        .replace(/<div class="signature-container">[\s\S]*$/, "")
        .trim();
      setContent(contentWithoutSignature);
      setBannerImage(document.banner_image);
      {
        const docSigners = Array.isArray(document.signers) ? document.signers : [];
        if (docSigners.length > 0) {
          setMultiSignerMode(true);
          setSigners(docSigners.map((s) => ({ name: s.name || "" })));
          setHasSignatureBox(false);
        } else {
          setMultiSignerMode(false);
          setHasSignatureBox(document.content.includes("signature-container"));
        }
      }
      setDocumentType(document.document_type || "standard");
      setActiveTemplate(document.document_type || "standard");

      // Only reset states if there were no changes in progress
      if (!hadChanges) {
        setIsSaved(true);
        setHasChanges(false);
      }
    }
  }, [document]);

  const handleDownloadPDF = async () => {
    try {
      await downloadDocument();
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  // Add this function to handle back navigation
  const handleBack = () => {
    // Check if we came from a client detail page
    const params = new URLSearchParams(location.search);
    const clientId = params.get("clientId");

    if (clientId) {
      // Navigate back to client detail page
      navigate(`/clients/${clientId}`);
    } else {
      // Default navigation to documents list
      navigate("/");
    }
  };

  const signatureBoxHtml = `
    <div class="signature-container">
      <!-- Business Signature Section -->
      <div class="business-signature-section">
        <div class="input-group">
          <label><strong>Prepared by:</strong> Carter James, Owner/Founder of GrayTecknologies LLC</label>
        </div>
        <canvas id="business-signature-pad" width="500" height="100"></canvas>
        <p><strong>Date:</strong> <span id="business-sign-date"></span></p>
      </div>
      
      <!-- Divider -->
      <hr style="margin: 20px 0; border: 1px solid #e2e8f0;">
      
      <!-- Client Signature Section -->
      <div class="client-signature-section">
        <div class="input-group">
          <label for="name-input"><strong>Point of Contact Name:</strong></label>
          <input type="text" id="name-input" class="poc-input">
        </div>
        <canvas id="signature-pad" width="500" height="100"></canvas>
        <div class="button-group">
          <button id="clear-button" class="action-button">Clear Signature</button>
          <button id="sign-button" class="action-button" disabled>Sign Document</button>
          <button id="download-button" class="action-button">Download as PDF</button>
        </div>
        <p><strong>Date Signed:</strong> <span id="sign-date"></span></p>
      </div>
    </div>
  `;

  const initializeSignaturePad = () => {
    if (previewRef.current && window.SignaturePad) {
      // Handle Business Signature (Auto-generated)
      const businessCanvas = previewRef.current.querySelector("#business-signature-pad");
      const businessSignDateElement = previewRef.current.querySelector("#business-sign-date");
      
      if (businessCanvas) {
        // Auto-generate business signature
        const businessCtx = businessCanvas.getContext("2d");
        if (businessCtx) {
          businessCtx.clearRect(0, 0, businessCanvas.width, businessCanvas.height);
          businessCtx.font = "italic 30px 'Brush Script MT', cursive";
          businessCtx.fillStyle = "black";
          businessCtx.fillText("Carter James", 20, businessCanvas.height / 2);
        }
        
        // Set document creation/publish date for business signature
        if (businessSignDateElement) {
          let businessSignDate;
          if (document?.created_at) {
            businessSignDate = new Date(document.created_at).toLocaleDateString();
          } else {
            // Fallback to current date if no document date available
            businessSignDate = new Date().toLocaleDateString();
          }
          businessSignDateElement.textContent = businessSignDate;
        }
      }

      // Handle Client Signature (Interactive)
      const canvas = previewRef.current.querySelector("#signature-pad");
      if (!canvas) {
        console.error("Client signature pad canvas not found");
        return;
      }

      const signaturePad = new window.SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 3,
        penColor: "black",
      });

      const clearButton = previewRef.current.querySelector("#clear-button");
      const signDateElement = previewRef.current.querySelector("#sign-date");
      const nameInput = previewRef.current.querySelector("#name-input");
      const signButton = previewRef.current.querySelector("#sign-button");

      const clearAll = () => {
        signaturePad.clear();
        if (signDateElement) {
          signDateElement.textContent = "";
        }
        if (nameInput) {
          nameInput.value = "";
        }
        if (signButton) {
          signButton.disabled = true;
        }
      };

      if (clearButton) {
        clearButton.addEventListener("click", clearAll);
      }

      const generateSignature = (name) => {
        if (!canvas) {
          console.error("Canvas not found when generating signature");
          return;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          console.error("Unable to get 2D context from canvas");
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = "italic 30px 'Brush Script MT', cursive";
        ctx.fillStyle = "black";
        ctx.fillText(name, 20, canvas.height / 2);
      };

      if (nameInput && signButton) {
        nameInput.addEventListener("input", function () {
          const name = this.value.trim();
          signButton.disabled = name === "";
          if (name !== "") {
            generateSignature(name);
          } else {
            signaturePad.clear();
          }
        });
      }

      if (signButton) {
        signButton.addEventListener("click", () => {
          const signDate = new Date().toLocaleDateString();
          if (signDateElement) {
            signDateElement.textContent = signDate;
          }
          alert("Document signed on " + signDate);
        });
      }

      const downloadButton =
        previewRef.current.querySelector("#download-button");
      if (downloadButton) {
        downloadButton.addEventListener("click", async () => {
          try {
            await downloadDocument(); // Use the hook instead of direct fetcher
          } catch (error) {
            console.error("Failed to download PDF:", error);
          }
        });
      }
    }
  };

  //   useEffect(() => {
  //     if (!isInitialMount.current) {
  //       const currentContent = hasSignatureBox ? content + signatureBoxHtml : content;
  //       const documentContent = document?.content || '';

  //       const hasContentChanged = currentContent !== documentContent;
  //       const hasTitleChanged = title !== document?.title;
  //       const hasDescriptionChanged = description !== (document?.description || '');
  //       const hasBannerChanged = bannerImage !== document?.banner_image;
  //       const hasSignatureBoxChanged = hasSignatureBox !== document?.content.includes('signature-container');

  //       const hasAnyChange =
  //         hasContentChanged ||
  //         hasTitleChanged ||
  //         hasDescriptionChanged ||
  //         hasBannerChanged ||
  //         hasSignatureBoxChanged;

  //       setHasChanges(hasAnyChange);
  //       if (hasAnyChange) {
  //         setIsSaved(false);
  //       }
  //     } else {
  //       isInitialMount.current = false;
  //     }
  //   }, [title, content, description, bannerImage, hasSignatureBox, document]);

  // useEffect(() => {
  //   if (document) {
  //     setTitle(document.title);
  //     setContent(document.content);
  //     setBannerImage(document.banner_image);

  //     // Check if the document content contains the signature box
  //     if (document.content.includes('signature-container')) {
  //       setHasSignatureBox(true);
  //       // Remove the signature box from the content for editing
  //       setContent(document.content.replace(/<div class="signature-container">[\s\S]*?<\/p>/, ''));
  //     } else {
  //       setHasSignatureBox(false);
  //       setContent(document.content);
  //     }
  //   }
  // }, [document]);

  //   const handleSave = () => {
  //     onSave({ title, content: getFullContent(), bannerImage });
  // const handleCancel = () => {
  //     navigate('/');
  // };

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const addCompanyBranding = (documentContent) => {
    const brandingHtml = `
      <div class="company-branding">
        <p>GrayTecknologies LLC | Innovative Web Solutions</p>
      </div>
    `;
    return documentContent + brandingHtml;
  };

  // Strip the document-wrapper closing </div> + </body></html> so appended
  // elements (signature box, footer) stay inside the document-wrapper.
  // Only strips one </div> directly before </body> to avoid removing
  // inner container closing tags (highlight-box, section divs, etc).
  const stripClosingHtmlTags = (html) => {
    return html.replace(/<\/div>\s*<\/body>\s*<\/html>\s*$/i, '');
  };

  const getFullContent = () => {
    let fullContent = "";
    if (activeTemplate === "capabilities") {
      // Capabilities template cover page
      fullContent += `
        <div class="cover-page">
          <h1>${title}</h1>
          <h2>${subtitle}</h2>
          <p>${tagline}</p>
          ${secondaryTagline ? `<p>${secondaryTagline}</p>` : ""}
        </div>
      `;

      // Add main content (strip closing tags so signature stays inside body)
      fullContent += stripClosingHtmlTags(content);

      // Add signature box if needed
      if (hasSignatureBox) {
        fullContent += signatureBoxHtml;
      } else if (multiSignerMode) {
        fullContent += buildMultiSignerPreview();
      }

      // Add company footer
      fullContent += `
        <div class="company-footer">
          <div class="company-name">GrayTecknologies LLC</div>
          <div class="company-tagline">Innovative Web Solutions</div>
        </div>
      `;
    } else if (activeTemplate === "proposal") {
      // Capabilities template cover page
      fullContent += `
        <div class="cover-page">
          <h1>${title}</h1>
          <h2>${subtitle}</h2>
          <p>Prepared for: ${tagline}</p>
          ${
            secondaryTagline
              ? `<p>${secondaryTagline}${
                  clientCompany ? `, ${clientCompany}` : ""
                }</p>`
              : ""
          }
        </div>
      `;

      // Add main content (strip closing tags so signature stays inside body)
      fullContent += stripClosingHtmlTags(content);

      // Add signature box if needed
      if (hasSignatureBox) {
        fullContent += signatureBoxHtml;
      } else if (multiSignerMode) {
        fullContent += buildMultiSignerPreview();
      }

      // Add company footer
      fullContent += `
        <div class="company-footer">
          <div class="company-name">GrayTecknologies LLC</div>
          <div class="company-tagline">Innovative Web Solutions</div>
        </div>
      `;
    } else {
      // Add banner image if exists
      if (bannerImage) {
        fullContent += `<div class="document-banner"><img src="${bannerImage}" alt="Document Banner" /><div class="banner-title-overlay"><h1 class="banner-title">${title}</h1><div class="banner-title-line"></div></div></div>`;
      } else {
        fullContent += `<h1 style="margin-top: 1.5em; margin-bottom: 1em;">${title}</h1>`;
      }

      // Add main content (strip closing tags so signature stays inside body)
      fullContent += `<div class="document-body">${stripClosingHtmlTags(content)}`;

      if (hasSignatureBox) {
        fullContent += signatureBoxHtml;
      } else if (multiSignerMode) {
        fullContent += buildMultiSignerPreview();
      }

      // Add a subtle line break
      fullContent += '<hr class="footer-divider">';

      // Add enhanced company branding
      fullContent += `
        <div class="company-branding">
          <p class="company-name">GrayTecknologies LLC</p>
          <p class="company-tagline">Innovative Web Solutions</p>
        </div>
      `;

      fullContent += "</div>";
    }

    return fullContent;
  };

  // Lightweight marker appended for multi-signer docs. The backend fills it with
  // one signature line per signer (driven by the saved signers list), the same
  // way the single signature box is appended by its button.
  const multiSignerMarker = '<div class="signature-container"></div>';

  // Editor-only preview of the multi-signer block, so the live preview reflects
  // what the published document will show (the real block is rendered server-side).
  const buildMultiSignerPreview = () => {
    const named = signers
      .map((s) => (s.name || "").trim())
      .filter((name) => name.length > 0);
    const rows = (named.length ? named : ["Signer 1", "Signer 2"])
      .map(
        (name) => `
        <div class="client-signature-section">
          <div class="input-group">
            <label><strong>Signer: ${name}</strong></label>
          </div>
          <canvas class="signature-pad-canvas" width="500" height="100"></canvas>
        </div>`
      )
      .join("");
    return `
      <div class="signature-container unsigned">
        <div class="business-signature-section">
          <div class="input-group">
            <label><strong>Prepared by:</strong> Carter James, Owner/Founder of GrayTecknologies LLC</label>
          </div>
          <div class="business-signature-display">
            <span style="font-family: 'Brush Script MT', cursive; font-style: italic; font-size: 24px;">Carter James</span>
          </div>
        </div>
        <hr style="margin: 20px 0; border: 1px solid #e2e8f0;">
        ${rows}
      </div>`;
  };

  const toggleSignatureBox = () => {
    const next = !hasSignatureBox;
    setHasSignatureBox(next);
    if (next) {
      // Single and multi are mutually exclusive.
      setMultiSignerMode(false);
    }
    setHasChanges(true); // Explicitly set hasChanges when signature box is toggled
    setIsSaved(false); // Indicate that there are unsaved changes
  };

  const toggleMultiSigner = () => {
    const next = !multiSignerMode;
    setMultiSignerMode(next);
    if (next) {
      // Single and multi are mutually exclusive.
      setHasSignatureBox(false);
      if (signers.length < 2) {
        setSigners([{ name: "" }, { name: "" }]);
      }
    }
    setHasChanges(true);
    setIsSaved(false);
  };

  const updateSignerName = (index, name) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, name } : s)));
    setHasChanges(true);
    setIsSaved(false);
  };

  const addSigner = () => {
    setSigners((prev) => [...prev, { name: "" }]);
    setHasChanges(true);
    setIsSaved(false);
  };

  const removeSigner = (index) => {
    setSigners((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    setHasChanges(true);
    setIsSaved(false);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBannerImage = () => {
    setBannerImage(null);
  };

  const previewStyles = `
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background-color: #f0f0f0;
      margin: 0;
      padding: 10px;
    }
    @media (min-width: 640px) {
      body {
        padding: 20px;
      }
    }
    .document-container {
      max-width: 800px;
      margin: 16px auto;
      background-color: white;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
      border-radius: 8px;
      overflow: hidden;
      position: relative;
    }
    @media (min-width: 640px) {
      .document-container {
        margin: 40px auto;
      }
    }
    .document-content {
      padding: 0;
      line-height: 1.6;
      color: black;
    }
    .document-content::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to right bottom, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 60%);
      pointer-events: none;
    }
    .back-button {
      position: fixed;
      top: 20px;
      left: 20px;
      padding: 10px 20px;
      background-color: #333;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 16px;
    }
    .back-button:hover {
      background-color: #555;
    }
    h1 {
      color: #4a5568;
      font-size: 24px;
      margin-bottom: 20px;
      text-align: center;
    }
    h2 {
      color: #4a5568;
      font-size: 20px;
      margin-top: 30px;
      margin-bottom: 15px;
    }
    p {
      margin-bottom: 10px;
    }
    strong {
      font-weight: bold;
    }
    ol, ul {
      margin-bottom: 15px;
      padding-left: 30px;
    }
    ol {
      list-style-type: decimal;
    }
    ul {
      list-style-type: disc;
    }
    li {
      margin-bottom: 5px;
      padding-left: 5px;
    }
    ol ol, ul ul, ol ul, ul ol {
      margin-top: 5px;
      margin-bottom: 5px;
    }
    .company-header {
      text-align: center;
      margin-bottom: 30px;
    }
    .company-name {
      font-size: 22px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .company-tagline {
      font-style: italic;
      color: #718096;
    }
    .signature-container {
      margin-top: 30px;
      border-top: 1px solid #e2e8f0;
      padding-top: 20px;
    }
    .business-signature-section {
      margin-bottom: 20px;
    }
    .client-signature-section {
      margin-top: 20px;
    }
    .input-group {
      margin-bottom: 10px;
    }
    .poc-input {
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 16px;
    }
    #signature-pad, #business-signature-pad {
      border: 1px solid #000;
      width: 100%;
      max-width: 100%;
      height: auto;
      margin-bottom: 10px;
    }
    #business-signature-pad {
      background-color: #f8f9fa;
    }
    .button-group {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: space-between;
    }
    .action-button {
      padding: 10px 15px;
      background-color: #4a5568;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.3s;
    }
    .action-button:hover {
      background-color: #2d3748;
    }
    .action-button:disabled {
      background-color: #a0aec0;
      cursor: not-allowed;
    }
    .company-branding {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e2e8f0;
      text-align: center;
      font-size: 14px;
      color: #718096;
    }
    .document-banner {
      width: 100%;
      height: 200px;
      overflow: hidden;
      position: relative;
    }
    .document-banner img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .banner-title-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      text-align: center;
      padding: 16px 24px 20px;
      background: linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%);
    }
    .banner-title {
      color: #ffffff !important;
      font-size: 20px !important;
      font-weight: bold !important;
      font-family: Georgia, serif !important;
      margin: 0 0 8px 0 !important;
      text-shadow: 0 1px 4px rgba(0,0,0,0.5);
      letter-spacing: 0.01em;
    }
    @media (min-width: 640px) {
      .banner-title {
        font-size: 26px !important;
      }
    }
    .banner-title-line {
      width: 55%;
      height: 1px;
      background: rgba(255,255,255,0.85);
      margin: 0 auto;
    }
    .document-body {
      padding: 16px;
      color: #333; /* Ensure text color is dark */
    }
    @media (min-width: 640px) {
      .document-body {
        padding: 40px;
      }
    }
    h1 {
      color: #4a5568;
      font-size: 24px;
      margin-top: 0;
      margin-bottom: 20px;
      text-align: center;
    }
    p, ul, ol, li {
      color: #333; /* Ensure all text elements have a dark color */
    }
    .footer-divider {
      border: 0;
      background: rgba(0, 0, 0, 0.1);
      margin: 20px 0 10px;
    }

    .company-branding {
      text-align: center;
      margin-top: 10px;
      color: #555;
      font-size: 14px;
    }

    .company-name {
      font-weight: bold;
      margin-bottom: 2px;
    }

    .company-tagline {
      font-style: italic;
    }

    @media print {
      .footer-divider,
      .company-branding {
        position: fixed;
        bottom: 20px;
        left: 0;
        right: 0;
      }
    }
  `;

  useEffect(() => {
    if (isPreviewMode) {
      return loadSignaturePadScript();
    }
  }, [isPreviewMode]);

  // If document is signed, show the preview mode
  if (document?.signed_by) {
    console.log(
      "Document is signed, passing to SignedDocumentPreview:",
      document
    );
    return (
      <SignedDocumentPreview
        document={document}
        onBack={handleBack}
        // onPublish={handlePublish}
      />
    );
  }

  if (isLoading) return <div>Loading...</div>;
  if (isError)
    return (
      <ErrorPage
        title="Document Error"
        message="Failed to load the document. Please try again later."
        errorCode="500"
      />
    );

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 bg-gray-100 dark:bg-darkbg-900 min-h-screen">
      {isPreviewMode ? (
        <div className="fixed inset-0 bg-gray-50 dark:bg-darkbg-900 overflow-auto">
          {activeTemplate === "capabilities" ||
          activeTemplate === "proposal" ? (
            <CapabilitiesStyles />
          ) : (
            <style>{previewStyles}</style>
          )}{" "}
          <button
            onClick={togglePreviewMode}
            className="fixed top-2 left-2 sm:top-4 sm:left-4 px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base bg-white dark:bg-darkbg-800 shadow-lg rounded-md flex items-center gap-1.5 sm:gap-2 z-50 hover:bg-gray-50 dark:hover:bg-darkbg-700 transition-colors"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Back to Editor
          </button>
          {activeTemplate === "capbilities" || "proposal" ? (
            <div className="max-w-[1200px] mx-auto px-2 sm:px-4">
              <div
                ref={previewRef}
                className="document-content"
                dangerouslySetInnerHTML={{ __html: getFullContent() }}
              />
            </div>
          ) : (
            <div className="max-w-[1200px] mx-auto my-4 sm:my-8 px-2 sm:px-0 bg-white dark:bg-darkbg-800 shadow-xl rounded-lg overflow-hidden">
              <div
                ref={previewRef}
                className="document-content"
                dangerouslySetInnerHTML={{ __html: getFullContent() }}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Back Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBack}
            className="mb-4 flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 transition-colors duration-200 shadow-md text-sm sm:text-base"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            {location.search.includes("clientId")
              ? "Back to Client"
              : "Back to Documents"}
          </motion.button>

          {/* Progress Tracker */}
          {!isLoading && !isError && (
            <>
              <ProgressTracker document={document || {}} />
              <DocumentAnalytics document={document || {}} />
            </>
          )}

          {/* Main Editor Container */}
          <div className="bg-white dark:bg-darkbg-800 shadow-lg rounded-lg p-3 sm:p-6">
            <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-grayteck-900 dark:text-grayteck-100 border-b pb-2">
              {id ? "Edit Document" : "Create Document"}
            </h1>

            {/* Form Fields */}
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label
                  htmlFor="client"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                >
                  Client
                </label>
                <ClientSearchSelect
                  selectedClientId={selectedClient}
                  onClientSelect={(value, clientData) => {
                    setSelectedClient(value);
                    setSelectedClientData(clientData);
                  }}
                  onAddNew={() => setIsAddClientModalOpen(true)}
                />
              </div>
              {/* Title Input */}
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                >
                  Document Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 p-2 text-sm sm:text-base"
                  placeholder="Enter document title"
                />
              </div>

              {/* Additional Form Fields for Capabilities Template */}
              {(activeTemplate === "capabilities" ||
                activeTemplate === "proposal") && (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="subtitle"
                      className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                    >
                      Document Subtitle
                    </label>
                    <input
                      type="text"
                      id="subtitle"
                      value={subtitle}
                      onChange={(e) => setSubtitle(e.target.value)}
                      className="w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 p-2 text-sm sm:text-base"
                      placeholder="e.g., Technical Capabilities Statement"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="tagline"
                      className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                    >
                      {documentType === "proposal"
                        ? "Point of Contact"
                        : "Document Tagline"}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="tagline"
                        value={tagline}
                        onChange={(e) => setTagline(e.target.value)}
                        className={`w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 
                    dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 
            p-2 text-sm sm:text-base ${
              selectedClient && activeTemplate === "proposal"
                ? "border-blue-300 dark:border-blue-600"
                : ""
            }`}
                        placeholder={
                          documentType === "proposal"
                            ? "Enter Point of Contact Name"
                            : "e.g., Empowering Businesses Through Technology"
                        }
                      />
                      {selectedClient && activeTemplate === "proposal" && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 dark:text-blue-400">
                          Auto-filled
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="secondaryTagline"
                      className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                    >
                      {documentType === "proposal"
                        ? "Point of Contact Role"
                        : "Secondary Tagline (Optional)"}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="secondaryTagline"
                        value={secondaryTagline}
                        onChange={(e) => setSecondaryTagline(e.target.value)}
                        className={`w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 
                    dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 
                    p-2 text-sm sm:text-base ${
                      selectedClient
                        ? "border-blue-300 dark:border-blue-600"
                        : ""
                    }`}
                        placeholder={
                          documentType == "proposal"
                            ? "Enter Point of Contact Role"
                            : "e.g., Innovative Web Solutions"
                        }
                      />
                      {selectedClient && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 dark:text-blue-400">
                          Auto-filled
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Client Dropdown (Only for Proposal Template) */}

                  {/* Optionally, Add New Client Form via Modal */}
                  <Modal
                    isOpen={isAddClientModalOpen}
                    onClose={() => setIsAddClientModalOpen(false)}
                  >
                    <AddClientForm
                      onClose={() => setIsAddClientModalOpen(false)}
                      isOpen={isAddClientModalOpen}
                    />
                  </Modal>
                </div>
              )}

              {/* Description Input */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                >
                  Document Description (optional)
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 p-2 text-sm sm:text-base"
                  placeholder="Enter document description (optional)"
                  rows="3"
                ></textarea>
                <label
                  htmlFor="banner-image"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                >
                  Banner Image
                </label>
                <div className="mt-1 flex justify-center px-3 sm:px-6 pt-4 sm:pt-5 pb-4 sm:pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50 dark:bg-darkbg-700 dark:border-darkbg-600">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-grayteck-600 hover:text-grayteck-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-grayteck-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleImageUpload}
                          accept="image/*"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>
                {bannerImage && (
                  <div className="mt-4 p-3 sm:p-4 bg-gray-100 dark:bg-darkbg-700 rounded-md shadow-inner">
                    <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                      <img
                        src={bannerImage}
                        alt="Banner preview"
                        className="w-full sm:w-40 h-24 object-cover rounded"
                      />
                      <div className="text-center sm:text-left">
                        <p className="text-sm text-grayteck-600 dark:text-grayteck-300 mb-2">
                          Banner image selected
                        </p>
                        <button
                          onClick={removeBannerImage}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 shadow-sm"
                        >
                          Remove Banner
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Content Input */}
              <div>
                <label
                  htmlFor="content"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-2"
                >
                  Document Content (HTML)
                </label>

                <TemplateSelector
                  activeTemplate={activeTemplate}
                  onTemplateChange={(templateType) => {
                    setDocumentType(templateType);
                    setActiveTemplate(templateType);
                  }}
                />

                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onBlur={() => {
                    if (isHtmlUnformatted(content)) {
                      setContent(formatHtml(content));
                    }
                  }}
                  rows="10"
                  className="w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 p-2 text-sm sm:text-base"
                  // placeholder="Enter HTML content"
                  placeholder={
                    activeTemplate === "capabilities"
                      ? `<!-- Cover Page -->
          <div class="cover-page">
            <h1>Title</h1>
            <h2>Subtitle</h2>
          </div>
          
          <!-- Content Sections -->
          <div class="section">
            <h1>Section Title</h1>
            
            <!-- Service Cards -->
            <div class="service-grid">
              <div class="service-card">
                <h3>Service Title</h3>
                <p>Description</p>
              </div>
            </div>
          
            <!-- Highlight Box -->
            <div class="highlight-box">
              Important content here
            </div>
          
            <!-- Tech List -->
            <div class="tech-list">
              <span class="tech-item">Technology</span>
            </div>
          </div>`
                      : "Enter HTML content"
                  }
                />
              </div>
            </div>

            {/* Selection Requirements Settings (only for Proposals) */}
            {activeTemplate === "proposal" && (
              <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-darkbg-700 rounded-md shadow-inner">
                <h3 className="text-base sm:text-lg font-medium text-grayteck-900 dark:text-grayteck-100 mb-2">
                  Selection Requirements
                </h3>
                <div className="flex items-start sm:items-center mb-2">
                  <input
                    type="checkbox"
                    id="require-payment-selection"
                    checked={requirePaymentSelection}
                    onChange={() => setRequirePaymentSelection(!requirePaymentSelection)}
                    className="mr-2 mt-0.5 sm:mt-0 h-4 w-4 shrink-0 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="require-payment-selection" className="text-sm text-grayteck-700 dark:text-grayteck-300">
                    Require payment option selection before signing
                  </label>
                </div>
                <div className="flex items-start sm:items-center">
                  <input
                    type="checkbox"
                    id="require-support-selection"
                    checked={requireSupportSelection}
                    onChange={() => setRequireSupportSelection(!requireSupportSelection)}
                    className="mr-2 mt-0.5 sm:mt-0 h-4 w-4 shrink-0 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="require-support-selection" className="text-sm text-grayteck-700 dark:text-grayteck-300">
                    Require support package selection before signing
                  </label>
                </div>
              </div>
            )}

            {/* Multi-signer configuration (shown when multi-signer mode is on) */}
            {multiSignerMode && (
              <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-darkbg-700 rounded-md shadow-inner">
                <h3 className="text-base sm:text-lg font-medium text-grayteck-900 dark:text-grayteck-100 mb-1">
                  Required Signers
                </h3>
                <p className="text-xs text-grayteck-500 dark:text-grayteck-400 mb-3">
                  Each person below gets their own signature line. The document locks only after everyone signs.
                </p>
                {signers.map((s, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-grayteck-500 dark:text-grayteck-400 w-6 shrink-0">
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      value={s.name}
                      onChange={(e) => updateSignerName(index, e.target.value)}
                      placeholder={`Signer ${index + 1} full name`}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-darkbg-600 rounded-md text-sm bg-white dark:bg-darkbg-800 text-grayteck-900 dark:text-grayteck-100 focus:outline-none focus:ring-2 focus:ring-grayteck-400"
                    />
                    <button
                      type="button"
                      onClick={() => removeSigner(index)}
                      disabled={signers.length <= 1}
                      className="px-2 py-2 text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                      title="Remove signer"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSigner}
                  className="mt-1 text-sm text-grayteck-600 dark:text-grayteck-300 hover:underline"
                >
                  + Add another signer
                </button>
              </div>
            )}

            {/* Document Status Sections */}
            <PublishedLinkSection document={document} />

            {/* Action Buttons Container */}
            <div className="mt-6 border-t pt-4">
              {/* Primary Action Buttons - Stack on mobile, row on desktop */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  onClick={toggleSignatureBox}
                  disabled={multiSignerMode}
                  title={multiSignerMode ? "Turn off multi-signer to use a single signature box" : ""}
                  className={`w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-md text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed ${
                    hasSignatureBox
                      ? "bg-red-500 hover:bg-red-600 focus:ring-red-300"
                      : "bg-grayteck-500 hover:bg-grayteck-600 focus:ring-grayteck-300"
                  }`}
                >
                  {hasSignatureBox
                    ? "Remove Signature Box"
                    : "Add Signature Box"}
                </button>

                <button
                  onClick={toggleMultiSigner}
                  disabled={hasSignatureBox}
                  title={hasSignatureBox ? "Remove the single signature box to use multiple signers" : ""}
                  className={`w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-md text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed ${
                    multiSignerMode
                      ? "bg-red-500 hover:bg-red-600 focus:ring-red-300"
                      : "bg-grayteck-500 hover:bg-grayteck-600 focus:ring-grayteck-300"
                  }`}
                >
                  {multiSignerMode
                    ? "Remove Multi-Signer Box"
                    : "Add Multi-Signer Box"}
                </button>

                <button
                  onClick={togglePreviewMode}
                  className="w-full sm:w-auto px-4 py-2 bg-grayteck-500 text-white rounded-md hover:bg-grayteck-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grayteck-400 shadow-md text-sm sm:text-base"
                >
                  Preview
                </button>
                {/* Only show download button if document exists and has content */}
                {id && content && (
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isDownloading}
                    className="w-full sm:w-auto px-4 py-2 bg-grayteck-500 text-white rounded-md hover:bg-grayteck-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grayteck-400 shadow-md text-sm sm:text-base inline-flex items-center justify-center"
                  >
                    {isDownloading ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={handleSave}
                  disabled={isSaving || (isSaved && !hasChanges)}
                  className={`w-full sm:w-auto px-4 py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-grayteck-500 shadow-md text-sm sm:text-base ${
                    isSaving || (isSaved && !hasChanges)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {isSaving
                    ? "Saving..."
                    : isSaved && !hasChanges
                    ? "Saved"
                    : id
                    ? "Update Document"
                    : "Save Document"}
                </button>

                {/* Only show publish button if document isn't already published */}
                {/* Publish/Unpublish Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePublishAction}
                  disabled={isPublishActionLoading || !id}
                  className={`
            w-full sm:w-auto px-4 py-2 rounded-md 
            focus:outline-none focus:ring-2 focus:ring-offset-2 
            shadow-md text-sm sm:text-base
            flex items-center justify-center
            transition-colors duration-200
            ${
              document?.shareable_link
                ? "bg-orange-500 hover:bg-orange-600 focus:ring-orange-400"
                : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
            }
            ${
              isPublishActionLoading || !id
                ? "opacity-50 cursor-not-allowed"
                : ""
            }
            text-white
          `}
                >
                  {isPublishActionLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                      {document?.shareable_link
                        ? "Unpublishing..."
                        : "Publishing..."}
                    </>
                  ) : (
                    <>
                      {document?.shareable_link ? (
                        <>
                          <LinkIcon className="h-4 w-4 mr-2 rotate-90" />
                          Unpublish
                        </>
                      ) : (
                        <>
                          <LinkIcon className="h-4 w-4 mr-2" />
                          Publish
                        </>
                      )}
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default DocumentEditor;
